import React, { forwardRef } from "react";
import { useFormContext, RegisterOptions } from "react-hook-form";
import cn from "classnames";
import { useMergeRefs } from "storefront/hooks/useMergeRefs";
import { useFormControl } from "../FormControl";
import styles from "./Select.module.scss";

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "name"> {
  options: Array<SelectOption>;
  placeholder?: string;
  rules?: RegisterOptions;
  className?: string;
}

/**
 * Select component
 *
 * A form select component that integrates with react-hook-form.
 * Automatically registers with the form and displays validation states.
 */
const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, placeholder, rules, className, disabled, ...props }, ref) => {
    const {
      register,
      formState: { isSubmitting },
    } = useFormContext();
    const { name, error } = useFormControl();

    const { ref: hookFormRef, ...registerProps } = register(name, rules);

    const mergedRef = useMergeRefs(hookFormRef, ref);

    return (
      <select
        id={name}
        ref={mergedRef}
        className={cn(
          styles.select,
          { [styles.hasError]: !!error },
          { [styles.disabled]: disabled || isSubmitting },
          className,
        )}
        disabled={disabled || isSubmitting}
        {...registerProps}
        {...props}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
    );
  },
);

Select.displayName = "Select";

export default Select;
